import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Input } from '~/components/ui/inputs/Input';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import { useUpsertLocation } from '~/api/location';
import { useGetFacilityAddressOptions } from '~/api/facility';
import { addressOptionLabel } from './facilityLabel';

type EditableLocation = {
  id: string;
  name: string;
  code: string;
  branchCode?: string | null;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
  location?: EditableLocation;
};

type FormState = {
  name: string;
  code: string;
  branchCode: string;
  facilityId: string;
};

function toFormState(location?: EditableLocation): FormState {
  if (!location) return { name: '', code: '', branchCode: '', facilityId: '' };
  return {
    name: location.name,
    code: location.code,
    branchCode: location.branchCode ?? '',
    facilityId: '',
  };
}

export function UpsertLocationModal({
  isOpen,
  onClose,
  onSaved = () => {},
  location,
}: Props) {
  const { t } = useTranslation('settings');
  const isEditMode = Boolean(location);
  const needsAddressPicker = !isEditMode;
  const [form, setForm] = useState<FormState>(() => toFormState(location));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const upsertLocation = useUpsertLocation();
  const {
    data: facilitiesData,
    isLoading: facilitiesLoading,
    error: facilitiesError,
  } = useGetFacilityAddressOptions({ enabled: isOpen && needsAddressPicker });

  useEffect(() => {
    setForm(toFormState(location));
  }, [location]);

  function handleField(name: string, value: string) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleClose() {
    setForm(toFormState(location));
    setIsSubmitting(false);
    onClose();
  }

  const addressOptions = (facilitiesData?.organizationFacilities ?? []).map(
    (facility) => ({
      value: facility.id,
      label: addressOptionLabel(facility),
    })
  );

  const isValid =
    form.name.trim() &&
    form.code.trim() &&
    (isEditMode || form.facilityId.trim());

  async function handleSubmit() {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      await upsertLocation.mutateAsync({
        id: location?.id,
        facilityId: isEditMode ? undefined : form.facilityId,
        input: {
          name: form.name.trim(),
          code: form.code.trim(),
          branchCode: form.branchCode.trim() || null,
        },
      });
      toast.success(
        isEditMode
          ? t('settings.facilities.locations.update_success')
          : t('settings.facilities.locations.create_success')
      );
      onSaved();
      handleClose();
    } catch {
      toast.error(
        isEditMode
          ? t('settings.facilities.locations.update_error')
          : t('settings.facilities.locations.create_error')
      );
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-[25rem] max-w-full">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-prim-100 rounded-lg">
            <MapPin size={20} className="text-prim" />
          </div>
          <h2
            id="Modal-title"
            className="text-lg font-semibold text-neut-prim"
            role="heading"
          >
            {isEditMode
              ? t('settings.facilities.locations.edit_modal.title')
              : t('settings.facilities.locations.create_modal.title')}
          </h2>
        </div>

        <div className="space-y-4">
          <Input
            label={t('settings.facilities.locations.create_modal.name')}
            name="name"
            value={form.name}
            onChange={handleField}
            placeholder="Warehouse A"
            required
          />
          <Input
            label={t('settings.facilities.locations.create_modal.code')}
            name="code"
            value={form.code}
            onChange={handleField}
            placeholder="WH-A"
            required
          />
          <Input
            label={t('settings.facilities.locations.create_modal.branch_code')}
            name="branchCode"
            value={form.branchCode}
            onChange={handleField}
            placeholder="BR-001"
          />
          {needsAddressPicker && (
            <div>
              <SearchableSelect
                label={t('settings.facilities.locations.create_modal.address')}
                name="facilityId"
                value={form.facilityId}
                onChange={handleField}
                options={addressOptions}
                disabled={facilitiesLoading}
                placeholder={t(
                  'settings.facilities.locations.create_modal.address_placeholder'
                )}
                required
              />
              <p className="text-xs text-neut-sec mt-1">
                {facilitiesError
                  ? t(
                      'settings.facilities.locations.create_modal.address_error'
                    )
                  : t(
                      'settings.facilities.locations.create_modal.address_hint'
                    )}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-neut-prim border border-neut-200 rounded-lg hover:bg-neut-50 transition-colors"
            >
              {t('settings.facilities.locations.create_modal.cancel')}
            </button>
            <button
              type="button"
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-prim rounded-lg hover:bg-prim-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isEditMode
                ? t('settings.facilities.locations.edit_modal.submit')
                : t('settings.facilities.locations.create_modal.submit')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
